import React, {Fragment} from 'react';

import find from 'lodash/find';
import get from 'lodash/get';
import {PopoverBody} from 'reactstrap';

import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {StyledPopoverBottomDIV} from 'app/general/bootstrap/utils';
import {PopoverForm, PopoverHeading} from 'app/general/overlays';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import {capitalizeFirstLetter} from 'app/utils/strings';

import type {OverlayProps} from 'app/general/overlays';
import type {ContentType} from 'sweet-types';

interface CommonProps {
  title?: string;
  verb: string;
  gerundVerb: string;
  downloadLink: boolean;
  action: () => Promise<{result: string}>;
  onFinish?: () => void;
  overlayProps: OverlayProps;
  successMessage: string;
}

interface PropsWithContentType extends CommonProps {
  contentType: ContentType;
}

interface PropsWithObjType extends CommonProps {
  objType?: string;
}

type Props = PropsWithContentType | PropsWithObjType;

type StatusType =
  | {
      status: 'processing';
      result: void;
      error: void;
    }
  | {
      status: 'complete';
      result: string;
      error: any | void;
    }
  | {
      status: 'error';
      result: void;
      error: Record<string, unknown>;
    };

export function TaskActionDocumentPopover({
  title,
  verb,
  gerundVerb,
  downloadLink,
  action,
  onFinish,
  overlayProps,
  successMessage,
  ...other
}: Props): JSX.Element {
  const [status, setStatus] = React.useState<StatusType>({
    status: 'processing',
    result: undefined,
    error: undefined,
  });

  React.useEffect(() => {
    action().then(
      (response) => {
        const {result} = response;

        setStatus({
          status: 'complete',
          error: undefined,
          result,
        });

        if (onFinish != null) {
          onFinish();
        }

        return result;
      },
      (error) =>
        setStatus({
          status: 'error',
          result: undefined,
          error,
        })
    );
  }, []);

  const docType =
    'objType' in other
      ? other.objType
      : 'contentType' in other
      ? getContentType(other.contentType).name
      : 'document';

  const capitalizedVerb = capitalizeFirstLetter(verb);
  const capitalizedGerundVerb = capitalizeFirstLetter(gerundVerb);

  return (
    <PopoverForm overlayProps={overlayProps}>
      {({close}) => (
        <Fragment>
          <PopoverHeading>
            {title != null
              ? title
              : gt('%(verb)s %(docType)s', {
                  verb: capitalizedVerb,
                  docType,
                })}
          </PopoverHeading>
          <PopoverBody
            style={{
              width: 250,
            }}
          >
            {status.status === 'error' ? (
              <div className="text-danger text-center">
                <p className="font-weight-bold">
                  {getErrorMessage(
                    status.error,
                    gt('There was an error %(gerundVerb)s the %(docType)s', {
                      gerundVerb,
                      docType,
                    })
                  )}
                  .
                </p>
                <p>{gt('Please try again later')}.</p>
              </div>
            ) : status.status === 'complete' ? (
              <div>
                <p>{successMessage}</p>
                <p className="text-center">
                  {downloadLink ? (
                    <a
                      href={status.result}
                      rel="noopener noreferrer"
                      target="_blank"
                      className="btn btn-primary"
                      onClick={close}
                    >
                      {gt('Download %(docType)s', {
                        verb: capitalizedVerb,
                        docType,
                      })}
                    </a>
                  ) : (
                    <FullHTMLLink
                      html_url={status.result}
                      className="btn btn-primary"
                      onClick={close}
                    >
                      {gt('View %(docType)s', {
                        docType,
                      })}
                    </FullHTMLLink>
                  )}
                </p>
              </div>
            ) : (
              <div>
                <p>
                  {gt(
                    'This could take some seconds, your %(docType)s will appear here when finished',
                    {
                      docType,
                    }
                  )}
                  .
                </p>
                <p className="text-center">
                  <b>
                    <i className="fa fa-circle-notch fa-spin" />{' '}
                    {gt('%(gerundVerb)s %(docType)s', {
                      gerundVerb: capitalizedGerundVerb,
                      docType,
                    })}
                    ...
                  </b>
                </p>
              </div>
            )}
          </PopoverBody>
          <StyledPopoverBottomDIV>
            <button onClick={close} className="btn btn-link btn-sm">
              <i className="fa fa-times" /> {gt('Close')}
            </button>
          </StyledPopoverBottomDIV>
        </Fragment>
      )}
    </PopoverForm>
  );
}

function getErrorMessage(error: Record<string, any>, fallback: string): string {
  if (error.response != null && parseInt(error.response?.status) === 500) {
    return fallback;
  }

  if (error.data != null && typeof error.data === 'string') {
    return fallback;
  }

  const message = find(
    [
      (error) => get(error, 'data.detail'),
      (error) => get(error, 'data.0'),
      (error) => error.message,
    ].map((func) => func(error))
  );
  return message || fallback;
}
